import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
} from '@mui/material'
import { contactsApi, Contact, ApiError } from '../../services/api'
import { CONNECTION_TYPE_OPTIONS, DEFAULT_CONNECTION_TYPE, ConnectionType } from '../../constants/networking'
import { formatPhoneInput, isValidPhone, toE164 } from '../../utils/formatPhone'

interface DuplicateWarning {
  reason: 'email' | 'nameCompany'
  contact: { id: string; firstName: string; lastName: string; company: string; email: string }
}

const describeDuplicate = ({ reason, contact }: DuplicateWarning) => {
  const name = `${contact.firstName} ${contact.lastName}`.trim()
  const at = contact.company ? ` at ${contact.company}` : ''
  return reason === 'email'
    ? `${name}${at} already uses ${contact.email}.`
    : `${name}${at} is already in this client's network.`
}

interface AddContactDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  onCreated: (contact: Contact) => void
  referredBy?: { contactId: string; name: string }
}

export default function AddContactDialog({ open, onClose, clientId, onCreated, referredBy }: AddContactDialogProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [company, setCompany] = useState('')
  const [position, setPosition] = useState('')
  const [email, setEmail] = useState('')
  const [linkedinUrl, setLinkedinUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [connectionType, setConnectionType] = useState<ConnectionType>(DEFAULT_CONNECTION_TYPE)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  // MWA-326 — new-contact answers 409 when this person looks like someone the
  // client already has. It is a warning, not a refusal: two colleagues can
  // share a name at one company, so the decision is the user's.
  const [duplicate, setDuplicate] = useState<DuplicateWarning | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (force: boolean) => {
    if (!firstName.trim() || !lastName.trim()) return
    setSaving(true)
    setError(null)
    try {
      const contact = await contactsApi.create({
        clientId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: company.trim(),
        position: position.trim(),
        email: email.trim(),
        linkedinUrl: linkedinUrl.trim(),
        phone: phone.trim() ? toE164(phone) : '',
        connectionType: connectionType as Contact['connectionType'],
        notes: notes.trim(),
        ...(referredBy ? { referredBy: referredBy.name } : {}),
        ...(force ? { force: true } : {}),
      })
      onCreated(contact)
      handleClose()
    } catch (err) {
      console.error('Failed to create contact:', err)
      const details = err instanceof ApiError ? err.details : undefined
      if (err instanceof ApiError && err.status === 409 && details?.duplicate) {
        setDuplicate({
          reason: details.reason === 'email' ? 'email' : 'nameCompany',
          contact: details.duplicate as DuplicateWarning['contact'],
        })
      } else {
        // Previously this path only reached the console, so a failed save was
        // indistinguishable from a slow one.
        setError('Could not add this contact. Please try again.')
      }
    }
    setSaving(false)
  }

  const handleSubmit = () => submit(false)

  const handleClose = () => {
    setDuplicate(null)
    setError(null)
    setFirstName('')
    setLastName('')
    setCompany('')
    setPosition('')
    setEmail('')
    setLinkedinUrl('')
    setPhone('')
    setConnectionType(DEFAULT_CONNECTION_TYPE)
    setNotes('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{referredBy ? 'Add Referred Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {referredBy && (
            <Chip
              label={`Referred by ${referredBy.name}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Box>
          <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} fullWidth size="small" />
          <TextField label="Position" value={position} onChange={(e) => setPosition(e.target.value)} fullWidth size="small" />
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth size="small" />
          {/* Feeds the SMS outreach handoff, which prefills the recipient from
              this. Stored E.164 so the sms: link works from any locale. */}
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            error={!!phone.trim() && !isValidPhone(phone)}
            helperText={phone.trim() && !isValidPhone(phone) ? 'Enter a 10-digit number' : ''}
            placeholder="(555) 123-4567"
            fullWidth
            size="small"
          />
          <TextField label="LinkedIn URL" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} fullWidth size="small" />
          <FormControl size="small" fullWidth>
            <InputLabel>Connection Type</InputLabel>
            <Select
              value={connectionType}
              label="Connection Type"
              onChange={(e) => setConnectionType(e.target.value as ConnectionType)}
            >
              {CONNECTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} size="small" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {/* Phone blocks submit when it is non-empty and incomplete. The field
            shows "Enter a 10-digit number", and without this the button
            promised a check it did not make: toE164 returns its input
            unchanged when it cannot parse it, so a half-typed number was
            stored verbatim and then fed to the SMS handoff. */}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            saving ||
            !firstName.trim() ||
            !lastName.trim() ||
            (!!phone.trim() && !isValidPhone(phone))
          }
        >
          {saving ? 'Creating...' : referredBy ? 'Add Referred Contact' : 'Add Contact'}
        </Button>
      </DialogActions>

      {/* Deliberately a warning rather than a block: the same name at the same
          company can genuinely be two people, so the call is the user's. */}
      <Dialog open={!!duplicate} onClose={() => setDuplicate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Possible duplicate</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {duplicate ? describeDuplicate(duplicate) : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add them anyway if this is a different person.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicate(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => {
              setDuplicate(null)
              submit(true)
            }}
          >
            {saving ? 'Adding...' : 'Add anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
